/**
 * Production map plans (M73) — no catalog imports (avoids cycles with mapCatalog).
 */

import {
  archetypeLabel,
  complexityFromArchetype,
  type MapArchetype,
  type MapComplexity
} from './productionArchetypes'

export type ProductionMapStatus = 'active' | 'planned' | 'retired-fixture'

export interface ProductionMapPlan {
  id: string
  name: string
  archetype: MapArchetype
  complexity: MapComplexity
  status: ProductionMapStatus
  intendedUse: string
  inspiredBy?: string
}

/**
 * Target production catalog for Chapters 1+2 (~9 maps).
 * Planned ids are reserved; geometry lands in M74/M75.
 */
export const PRODUCTION_MAP_PLANS: ProductionMapPlan[] = [
  {
    id: 'valley-v',
    name: 'Valley',
    archetype: 'single-serpentine',
    complexity: 'A',
    status: 'active',
    intendedUse: 'Chapter 1 early teaching (1-1…1-4); familiar Ch2 refresher'
  },
  {
    id: 'crossroads-c',
    name: 'Crossroads',
    archetype: 'single-serpentine',
    complexity: 'A',
    status: 'active',
    intendedUse: 'Chapter 1 mid teaching corners; dense ground placement choices'
  },
  {
    id: 'switchback-s',
    name: 'Switchback',
    archetype: 'single-air',
    complexity: 'B',
    status: 'active',
    intendedUse: 'Tall single + long air; Chapter 2 familiar pressure'
  },
  {
    id: 'rift-r',
    name: 'Rift',
    archetype: 'single-air',
    complexity: 'B',
    status: 'active',
    intendedUse: 'Wide single + divergent air; Chapter 2 mid bands'
  },
  {
    id: 'twinspire',
    name: 'Twinspire',
    archetype: 'dual-independent',
    complexity: 'B',
    status: 'active',
    intendedUse: 'Forced distribution; late Ch1 / early–mid Ch2',
    inspiredBy: 'lab-dual'
  },
  {
    id: 'confluence',
    name: 'Confluence',
    archetype: 'dual-converge',
    complexity: 'C',
    status: 'active',
    intendedUse: 'Early lanes vs shared kill zone; Ch1 final / Ch2 mid',
    inspiredBy: 'lab-converge'
  },
  {
    id: 'delta',
    name: 'Delta',
    archetype: 'split-two-exit',
    complexity: 'C',
    status: 'active',
    intendedUse: 'Pre-split vs post-split specialization; Ch2 sparingly',
    inspiredBy: 'lab-split'
  },
  {
    id: 'triad',
    name: 'Triad',
    archetype: 'triple-seams',
    complexity: 'D',
    status: 'active',
    intendedUse: 'Pairwise seams only; late Ch2',
    inspiredBy: 'lab-triple'
  },
  {
    id: 'skew',
    name: 'Skew',
    archetype: 'air-cross',
    complexity: 'D',
    status: 'active',
    intendedUse: 'AA vs ground premium competition; Ch1 AA lesson / Ch2 air bands',
    inspiredBy: 'lab-air-cross'
  }
]

export function planForMapId(mapId: string): ProductionMapPlan | undefined {
  return PRODUCTION_MAP_PLANS.find((p) => p.id === mapId)
}

export function describePlan(plan: ProductionMapPlan) {
  return {
    ...plan,
    label: archetypeLabel(plan.archetype),
    complexity: plan.complexity || complexityFromArchetype(plan.archetype)
  }
}
